<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { getSavedCopyCountOfGoal } from '@/library/api'
import SaveToLibrary from '@/library/SaveToLibrary.vue'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { toolkitUrls } from '@/util/links'
import { useAnchorPageStore } from './anchor_page.store'

const SAVE_SUCCESS_DISPLAY_MS = 5000

type SavedGoal = { uuid: string }
type Folder = { uuid: string, name: string }
type SaveMessage = { theme: 'green' | 'red', role: 'status' | 'alert' }

const anchorPageStore = useAnchorPageStore()
const { content, savedAnchorPayload, actionPermissions, savedAnchor, saveErrorMessage } = storeToRefs(anchorPageStore)
const { userId } = storeToRefs(useUserStore())

const contentUuid = computed(() => content.value?.uuid ?? '')
const contentPayload = computed<Record<string, unknown>>(() => ({ ...savedAnchorPayload.value }))
const isSingleCopy = computed(() => savedCopyCount.value === 1)

const saveMessage = computed<SaveMessage | null>(() => {
  if (savedAnchor.value) {
    return { theme: 'green', role: 'status' }
  }
  if (saveErrorMessage.value) {
    return { theme: 'red', role: 'alert' }
  }
  return null
})

const savedCopyCount = ref(0)
let latestCountRequest = 0

async function refreshSavedCopyCount() {
  const thisRequest = ++latestCountRequest
  if (!userId.value || !contentUuid.value) return
  try {
    const count = await getSavedCopyCountOfGoal(userId.value, contentUuid.value)
    if (thisRequest !== latestCountRequest) return
    savedCopyCount.value = count
  } catch (error) {
    console.error('Error loading saved copy count of anchor page:', error)
  }
}

watch([userId, contentUuid], () => {
  savedCopyCount.value = 0
  refreshSavedCopyCount()
}, { immediate: true })

let saveSuccessTimer: ReturnType<typeof setTimeout> | undefined

function dismissSaveResult() {
  clearTimeout(saveSuccessTimer)
  anchorPageStore.resetSaveStatus()
}

onBeforeUnmount(dismissSaveResult)

function handleSave(folder: Folder, savedGoal: unknown) {
  dismissSaveResult()
  anchorPageStore.recordSaveSuccess({ studentName: folder.name, uuid: (savedGoal as SavedGoal).uuid })
  saveSuccessTimer = setTimeout(dismissSaveResult, SAVE_SUCCESS_DISPLAY_MS)
  refreshSavedCopyCount()
}

// The library API rejects with the server's parsed error body rather than an Error; both carry the message Vue 2 showed.
function errorMessageOf(error: unknown): string {
  const message = (error as { message?: unknown } | null)?.message
  return typeof message === 'string' ? message : String(error)
}

function handleSaveError(error: unknown) {
  dismissSaveResult()
  anchorPageStore.recordSaveError(errorMessageOf(error))
}
</script>

<template>
  <div>
    <SaveToLibrary
      :content-type="CONTENT_TYPES.SUPER_PAGE"
      content-type-label="Goal Page"
      :content-uuid="contentUuid"
      :content-title="content?.title"
      :content-payload="contentPayload"
      size="lg"
      :full-width="true"
      :action-permissions="actionPermissions.save"
      @save="handleSave"
      @saveerror="handleSaveError"
    />

    <div v-if="savedCopyCount > 0" data-testid="saved-copy-count" class="mt-3 text-center text-text-md text-gray-700">
      <span class="text-text-2xl font-bold">{{ savedCopyCount }}</span>
      {{ isSingleCopy ? 'version' : 'versions' }} of this page {{ isSingleCopy ? 'is' : 'are' }}
      <br />saved in <router-link :to="{ name: 'AllFolders' }" class="text-blue-400 hover:underline">Your Library</router-link>.
    </div>

    <slot />

    <Transition
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
      enter-active-class="transition-opacity motion-reduce:transition-none"
      leave-active-class="transition-opacity motion-reduce:transition-none"
    >
      <GbInlineMessage
        v-if="saveMessage"
        data-testid="save-message"
        :role="saveMessage.role"
        :gb-theme="saveMessage.theme"
        class="mt-2 w-full"
        :pt="{ text: { class: 'flex-1' } }"
        :pt-options="{ mergeProps: true }"
      >
        <div class="flex items-start gap-2 text-text-md">
          <span v-if="savedAnchor" class="flex-1 text-center">
            Saved to <span class="fs-exclude">{{ savedAnchor.studentName }}</span>!
            <a :href="toolkitUrls.libraryAnchorPage(savedAnchor.uuid)" class="text-blue-400 hover:underline">View saved anchor page</a>
          </span>
          <span v-else class="flex-1 text-center">{{ saveErrorMessage }}</span>
          <button
            type="button"
            aria-label="Close"
            class="material-icons-round text-text-xl leading-none text-gray-700 hover:text-gray-900"
            @click="dismissSaveResult"
          >close</button>
        </div>
      </GbInlineMessage>
    </Transition>
  </div>
</template>
